import JiraFieldMapper from '.';
import type { IssueTypeField } from '../getIssueTypeFields';
import { findJiraFieldByKey } from './findJiraFieldByKey';
import {
  IssueFieldSource,
  type TemplateMappingSchemaV1,
  type TemplateMappingSchemaV2
} from './types';

type InputType = TemplateMappingSchemaV1 | TemplateMappingSchemaV2;
type SelectedFields = {
  axeField: string;
  mappingField: string;
  sourceType: IssueFieldSource;
}[];

const mappers = Object.values(JiraFieldMapper);

class MappingSchema {
  read(
    input: InputType,
    fieldsSchema: IssueTypeField[]
  ): TemplateMappingSchemaV2 {
    const isV2Version = this.isV2(input);

    if (isV2Version) {
      return input;
    }

    const filteredFields = Object.entries(input).reduce<{
      v2Mapping: TemplateMappingSchemaV2['mapping'];
      unmappable: string[];
    }>(
      (result, [key, value]) => {
        const mapperId = mappers.find(mapper =>
          mapper.canMapThisField(fieldsSchema, key)
        )?.id;

        if (!mapperId) {
          result.unmappable.push(key);
          return result;
        }

        result.v2Mapping[key] = {
          sourceType: IssueFieldSource.Axe,
          source: value,
          mapperId
        };

        return result;
      },
      { v2Mapping: {}, unmappable: [] }
    );
    this.checkForUnprocessedFields(filteredFields.unmappable, fieldsSchema);

    return {
      dmversion: 2,
      dmversioned: true,
      mapping: filteredFields.v2Mapping,
      fields: fieldsSchema
    };
  }

  write(
    fields: SelectedFields,
    fieldsSchema: IssueTypeField[]
  ): TemplateMappingSchemaV2 {
    const filteredFields = fields.reduce<{
      mappable: TemplateMappingSchemaV2['mapping'];
      unmappable: string[];
    }>(
      (result, { axeField, mappingField, sourceType }) => {
        const mapperId = mappers.find(mapper =>
          mapper.canMapThisField(fieldsSchema, mappingField)
        )?.id;

        if (!mapperId) {
          result.unmappable.push(mappingField);
          return result;
        }

        result.mappable[mappingField] = {
          sourceType,
          source: axeField,
          mapperId
        };

        return result;
      },
      { mappable: {}, unmappable: [] }
    );

    this.checkForUnprocessedFields(filteredFields.unmappable, fieldsSchema);

    return {
      dmversion: 2,
      dmversioned: true,
      mapping: filteredFields.mappable,
      fields: fieldsSchema
    };
  }

  isV2(input: InputType): input is TemplateMappingSchemaV2 {
    return 'dmversion' in input && 'dmversioned' in input;
  }

  private checkForUnprocessedFields(
    fieldKeys: string[],
    fieldsSchema: IssueTypeField[]
  ) {
    const fullyUnmappableFields = fieldKeys.reduce<string[]>(
      (fields, field) => {
        const fieldEntity = findJiraFieldByKey(fieldsSchema, field);

        if (fieldEntity?.required && !fieldEntity?.hasDefaultValue) {
          fields.push(`'${fieldEntity.name}'`);
        }

        return fields;
      },
      []
    );

    if (fullyUnmappableFields.length) {
      throw new Error(
        `The template contains mapped required fields without default value that cannot be mapped: ${fullyUnmappableFields.join(
          ', '
        )}`
      );
    }
  }
}

export const AxeIssueMappingSchema = new MappingSchema();
